import { useCallback, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Download, CreditCard, Users, RefreshCw } from "lucide-react";
import { fetchGuardDirectory } from "../../services/guardDirectory";

interface Guard {
  id: string;
  name: string;
  email: string;
  employeeId: string;
  badgeDataUrl?: string;
}

const SAMPLE_GUARDS: Guard[] = [
  { id: "guard-001", name: "John Smith", email: "guard1@emory.local", employeeId: "EMP001" },
  { id: "guard-002", name: "Sarah Johnson", email: "guard2@emory.local", employeeId: "EMP002" },
  { id: "guard-003", name: "Mike Davis", email: "guard3@emory.local", employeeId: "EMP003" },
  { id: "guard-004", name: "Emily Wilson", email: "guard4@emory.local", employeeId: "EMP004" },
  { id: "guard-005", name: "David Brown", email: "guard5@emory.local", employeeId: "EMP005" }
];

export function GuardQRGenerator() {
  const [directory, setDirectory] = useState<Guard[]>([]);
  const [loadingDirectory, setLoadingDirectory] = useState(true);

  const [selectedGuard, setSelectedGuard] = useState<Guard | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [customGuard, setCustomGuard] = useState({ id: "", name: "", employeeId: "" });

  const loadDirectory = useCallback(async () => {
    setLoadingDirectory(true);
    try {
      const data = await fetchGuardDirectory();
      setDirectory(data);
    } catch (error) {
      console.error("Failed to load guard directory for badge generator", error);
    } finally {
      setLoadingDirectory(false);
    }
  }, []);

  useEffect(() => {
    void loadDirectory();
    const refresh = () => {
      void loadDirectory();
    };
    window.addEventListener("storage", refresh);
    window.addEventListener("guard-directory-updated", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("guard-directory-updated", refresh);
    };
  }, [loadDirectory]);

  const guards = directory.length ? directory : SAMPLE_GUARDS;

  const generateQRCode = async (guard: Guard) => {
    setSelectedGuard(guard);
    if (guard.badgeDataUrl) {
      setQrDataUrl(guard.badgeDataUrl);
      return;
    }
    const qrData = `GUARD:${guard.id}:${encodeURIComponent(guard.name)}`;
    try {
      const dataUrl = await QRCode.toDataURL(qrData, {
        width: 400,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#FFFFFF"
        }
      });
      setQrDataUrl(dataUrl);
    } catch (error) {
      console.error("Error generating QR code:", error);
    }
  };

  const downloadBadge = async () => {
    if (!selectedGuard || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size (standard badge size: 3.375" x 2.125" at 300 DPI)
    canvas.width = 1013;
    canvas.height = 638;

    // White background
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw border
    ctx.strokeStyle = "#1a365d";
    ctx.lineWidth = 4;
    ctx.strokeRect(10, 10, canvas.width - 20, canvas.height - 20);

    // Header
    ctx.fillStyle = "#1a365d";
    ctx.fillRect(10, 10, canvas.width - 20, 100);
    
    // Header text
    ctx.fillStyle = "white";
    ctx.font = "bold 48px Arial";
    ctx.textAlign = "center";
    ctx.fillText("EMORY SECURITY", canvas.width / 2, 70);

    // Guard name
    ctx.fillStyle = "#1a365d";
    ctx.font = "bold 56px Arial";
    ctx.textAlign = "center";
    ctx.fillText(selectedGuard.name.toUpperCase(), canvas.width / 2, 200);

    // Employee ID
    ctx.font = "36px Arial";
    ctx.fillText(`ID: ${selectedGuard.employeeId}`, canvas.width / 2, 260);

    // Load and draw QR code
    const qrImage = new Image();
    qrImage.onload = () => {
      // Draw QR code
      ctx.drawImage(qrImage, (canvas.width - 300) / 2, 300, 300, 300);

      // Footer
      ctx.fillStyle = "#6b7280";
      ctx.font = "24px Arial";
      ctx.textAlign = "center";
      ctx.fillText("Scan at checkpoint kiosks", canvas.width / 2, 630);

      // Download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `badge-${selectedGuard.employeeId}-${selectedGuard.name.replace(/\s+/g, "-").toLowerCase()}.png`;
          a.click();
          URL.revokeObjectURL(url);
        }
      });
    };
    qrImage.src = qrDataUrl;
  };

  const downloadAllBadges = async () => {
    for (const guard of guards) {
      await generateQRCode(guard);
      await new Promise((resolve) => setTimeout(resolve, 500));
      await downloadBadge();
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <CreditCard className="h-6 w-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-800">Guard Badge Generator</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void loadDirectory()}
            className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 ${loadingDirectory ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={downloadAllBadges}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Download className="h-4 w-4" />
            Download All Badges
          </button>
        </div>
      </div>

      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-800">
          Generate QR code badges for guards to scan at checkpoint kiosks. Each badge contains the guard's ID and name.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Users className="h-5 w-5" />
              Guards
            </h3>
            {loadingDirectory ? (
              <p className="text-sm text-gray-600">Loading guards…</p>
            ) : guards.length === 0 ? (
              <p className="text-sm text-gray-600">No guards available yet.</p>
            ) : (
              <div className="space-y-2">
                {guards.map((guard) => (
                  <button
                    key={guard.id}
                    onClick={() => generateQRCode(guard)}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                      selectedGuard?.id === guard.id
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className="font-semibold">{guard.name}</div>
                    <div className="text-sm text-gray-600">{guard.email || "—"}</div>
                    <div className="text-xs text-gray-500">ID: {guard.employeeId}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 p-4 bg-gray-50">
            <h4 className="text-sm font-semibold text-gray-800 mb-3">Generate custom badge</h4>
            <form
              className="space-y-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (!customGuard.id.trim() || !customGuard.name.trim()) return;
                const guard: Guard = {
                  id: customGuard.id.trim(),
                  name: customGuard.name.trim(),
                  employeeId: customGuard.employeeId.trim() || customGuard.id.trim(),
                  email: `${customGuard.id.trim()}@emory.local`
                };
                void generateQRCode(guard);
                setCustomGuard({ id: "", name: "", employeeId: "" });
              }}
            >
              <label className="text-xs font-medium text-gray-600">
                Guard ID
                <input
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                  value={customGuard.id}
                  onChange={(e) => setCustomGuard((prev) => ({ ...prev, id: e.target.value }))}
                  placeholder="e.g., G1234"
                  required
                />
              </label>
              <label className="text-xs font-medium text-gray-600">
                Guard name
                <input
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                  value={customGuard.name}
                  onChange={(e) => setCustomGuard((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Jordan Reed"
                  required
                />
              </label>
              <label className="text-xs font-medium text-gray-600">
                Employee ID (optional)
                <input
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                  value={customGuard.employeeId}
                  onChange={(e) => setCustomGuard((prev) => ({ ...prev, employeeId: e.target.value }))}
                  placeholder="EMP-0000"
                />
              </label>
              <button
                type="submit"
                className="w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
              >
                Generate QR
              </button>
            </form>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Badge Preview</h3>
          {selectedGuard && qrDataUrl ? (
            <div className="space-y-4">
              <div className="border-2 border-gray-200 rounded-lg p-6 bg-white">
                <div className="bg-blue-900 text-white p-3 -m-6 mb-4 rounded-t-lg">
                  <h4 className="text-center font-bold text-lg">EMORY SECURITY</h4>
                </div>
                <div className="text-center space-y-2 mb-4">
                  <div className="text-2xl font-bold">{selectedGuard.name}</div>
                  <div className="text-gray-600">ID: {selectedGuard.employeeId}</div>
                </div>
                <div className="flex justify-center mb-4">
                  <img src={qrDataUrl} alt="QR Code" className="w-48 h-48" />
                </div>
                <div className="text-center text-sm text-gray-500">
                  Scan at checkpoint kiosks
                </div>
              </div>
              
              <button
                onClick={downloadBadge}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Download className="h-4 w-4" />
                Download Badge
              </button>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-500">
              <CreditCard className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>Select a guard to generate their badge</p>
            </div>
          )}
        </div>
      </div>

      {/* Hidden canvas for badge generation */}
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
}
